import {
  RadioGroup as HeroUIRadioGroup,
  RadioGroupProps as HeroUIRadioGroupProps,
} from "@heroui/react";
import clsx from "clsx";
import { RadioTile, RadioTileProps } from "../radio-group-tile/radio-tile";

export type RadioTileGroupOption<
  T extends string | number | boolean = string | number,
> = Pick<
  RadioTileProps,
  "value" | "isDisabled" | "isInvalid" | "description"
> & { label: string; value: T };

type Color = Extract<HeroUIRadioGroupProps["color"], "primary">;
type Size = Extract<HeroUIRadioGroupProps["size"], "sm" | "lg">;

export type RadioTileGroupProps<
  T extends string | number | boolean = string | number,
> = Omit<
  HeroUIRadioGroupProps,
  "color" | "size" | "className" | "classNames"
> & {
  alignContent?: RadioTileProps["alignContent"];
  color?: Color;
  name: string;
  options?: RadioTileGroupOption<T>[];
  size?: Size;
};

export function RadioTileGroup<
  T extends string | number | boolean = string | number,
>({ options, ...props }: RadioTileGroupProps<T>) {
  return (
    <HeroUIRadioGroup
      {...props}
      classNames={{
        description: "text-gray-600",
        errorMessage: "text-danger-dark",
        label: clsx(
          "text-base text-small font-semibold text-default-900 after:text-default-900",
        ),
        wrapper: clsx("flex gap-4", {
          "flex-row gap-4": props.orientation === "horizontal",
        }),
      }}
      data-testid={props.name}
    >
      {options?.map((option) => (
        <RadioTile
          key={option.value}
          id={`${props.name}-${option.value}`}
          {...option}
          alignContent={props.alignContent}
          isInvalid={props.isInvalid}
          isRequired={props.isRequired}
          size={props.size}
        >
          {option.label}
        </RadioTile>
      ))}
    </HeroUIRadioGroup>
  );
}

RadioTileGroup.displayName = "RecareUI.RadioTileGroup";
